import React from 'react';
import { ArrowRight, Phone, Star } from 'lucide-react';
import { companyInfo } from '../data/mock';
import { useLanguage } from '../contexts/LanguageContext';

const Hero = () => {
  const { t } = useLanguage();
  
  const scrollToContact = () => {
    const element = document.getElementById('contacto');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section
      id="inicio"
      className="relative min-h-screen flex items-center justify-center overflow-hidden pt-32"
    >
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1541888946425-d81bb19240f5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxjb25zdHJ1Y3Rpb24lMjB3b3JrZXJzfGVufDB8fHx8MTc3NjQzMzM3OHww&ixlib=rb-4.1.0&q=85')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/95 via-slate-900/90 to-slate-800/85" />
        
        {/* Animated geometric patterns */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse delay-1000" />
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4">
        <div className="max-w-4xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-blue-500/20 backdrop-blur-sm border border-blue-500/30 rounded-full px-6 py-2 mb-8 animate-fade-in">
            <Star className="w-4 h-4 text-blue-400 fill-blue-400" />
            <span className="text-blue-300 font-semibold text-sm">
              {companyInfo.yearsExperience} {t('hero.badge')}
            </span>
            <Star className="w-4 h-4 text-blue-400 fill-blue-400" />
          </div>

          {/* Main Heading */}
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            {t('hero.title1')}
            <span className="block mt-2 bg-gradient-to-r from-blue-400 via-blue-400 to-blue-500 text-transparent bg-clip-text">
              {t('hero.title2')}
            </span>
          </h1>

          {/* Subheading */}
          <p className="text-xl md:text-2xl text-slate-300 mb-8 leading-relaxed">
            {t('hero.subtitle')}{' '}
            <span className="text-blue-400 font-semibold">New Jersey & New York</span>
          </p>

          {/* Features */}
          <div className="flex flex-wrap justify-center gap-6 mb-12">
            <div className="flex items-center gap-2 text-slate-200">
              <div className="w-2 h-2 bg-blue-500 rounded-full" />
              <span className="font-medium">{t('hero.freeEstimates')}</span>
            </div>
            <div className="flex items-center gap-2 text-slate-200">
              <div className="w-2 h-2 bg-blue-500 rounded-full" />
              <span className="font-medium">{t('hero.satisfactionGuaranteed')}</span>
            </div>
            <div className="flex items-center gap-2 text-slate-200">
              <div className="w-2 h-2 bg-blue-500 rounded-full" />
              <span className="font-medium">{companyInfo.projectsCompleted} {t('hero.projectsCompleted')}</span>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <button
              onClick={scrollToContact}
              className="group bg-blue-500 text-slate-900 px-8 py-4 rounded-xl font-bold text-lg hover:bg-blue-400 transition-all duration-300 hover:shadow-2xl hover:shadow-blue-500/50 hover:-translate-y-1 flex items-center gap-2"
            >
              {t('hero.ctaQuote')}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            
            <a
              href={`tel:${companyInfo.phone}`}
              className="group bg-slate-800 text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-slate-700 transition-all duration-300 border-2 border-slate-700 hover:border-blue-500 hover:shadow-xl flex items-center gap-2"
            >
              <Phone className="w-5 h-5 group-hover:rotate-12 transition-transform" />
              {companyInfo.phone}
            </a>
          </div>

          {/* Trust Indicators */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-400 mb-2">15+</div>
              <div className="text-slate-400 text-sm font-medium">{t('hero.yearsExperience')}</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-400 mb-2">500+</div>
              <div className="text-slate-400 text-sm font-medium">{t('hero.projectsCompleted')}</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-400 mb-2">100%</div>
              <div className="text-slate-400 text-sm font-medium">{t('hero.satisfaction')}</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-400 mb-2">8+</div>
              <div className="text-slate-400 text-sm font-medium">{t('hero.servicesSpecialized')}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-blue-500 rounded-full flex justify-center pt-2">
          <div className="w-1 h-3 bg-blue-500 rounded-full" />
        </div>
      </div>
    </section>
  );
};

export default Hero;
